import React from 'react';
import { Film, Download, MonitorPlay, Smartphone, Sparkles } from 'lucide-react';

export function MediaTab() {
  const MEDIA_BASE = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <div className="space-y-8">
      {/* Promo Videos */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Film size={22} className="text-gold-400" />
          <h3 className="text-2xl font-bold text-white">Promo Videos</h3>
        </div>
        <p className="text-teal-400 text-sm mb-6">Downloadable brand videos with audio · use anywhere</p>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Landscape Promo */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden" data-testid="media-landscape">
            <div className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <MonitorPlay size={18} className="text-teal-400" />
                <h4 className="text-white font-bold">Landscape Promo (16:9)</h4>
              </div>
              <p className="text-gray-400 text-xs">Perfect for YouTube, Facebook, X/Twitter, website embeds</p>
            </div>
            <div className="px-4">
              <video controls preload="metadata" className="w-full rounded-lg bg-black aspect-video">
                <source src={`${MEDIA_BASE}/media/promo-landscape.mp4`} type="video/mp4" />
              </video>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <a href={`${MEDIA_BASE}/media/promo-landscape.mp4`} download className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg text-sm font-medium transition-colors">
                <Download size={14} /> MP4 · 1280×720
              </a>
              <a href={`${MEDIA_BASE}/media/promo-landscape.webm`} download className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg text-sm font-medium transition-colors">
                <Download size={14} /> WebM · 1280×720
              </a>
            </div>
          </div>

          {/* Portrait Promo */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden" data-testid="media-portrait">
            <div className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Smartphone size={18} className="text-teal-400" />
                <h4 className="text-white font-bold">Portrait Promo (9:16)</h4>
              </div>
              <p className="text-gray-400 text-xs">Perfect for Instagram Reels, TikTok, YouTube Shorts</p>
            </div>
            <div className="px-4 flex justify-center">
              <video controls preload="metadata" className="rounded-lg bg-black" style={{ maxHeight: '300px', aspectRatio: '9/16' }}>
                <source src={`${MEDIA_BASE}/media/promo-portrait.mp4`} type="video/mp4" />
              </video>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <a href={`${MEDIA_BASE}/media/promo-portrait.mp4`} download className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg text-sm font-medium transition-colors">
                <Download size={14} /> MP4 · 720×1280
              </a>
              <a href={`${MEDIA_BASE}/media/promo-portrait.webm`} download className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg text-sm font-medium transition-colors">
                <Download size={14} /> WebM · 720×1280
              </a>
            </div>
          </div>
        </div>

        <div className="bg-teal-900/20 border border-teal-800/30 rounded-lg p-4 mt-4">
          <p className="text-teal-300 text-sm">
            <strong>Tip:</strong> MP4 is best for uploads to Instagram, TikTok, YouTube, and most social platforms. WebM is smaller and best for web embeds.
            Right-click any video to save the current frame as an image for thumbnails and posters.
          </p>
        </div>
      </div>

      {/* Social Post Presets */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Sparkles size={22} className="text-gold-400" />
          <h3 className="text-2xl font-bold text-white">Social Post Presets</h3>
        </div>
        <p className="text-teal-400 text-sm mb-6">Copy caption + download bundle (video + caption .txt) in one click</p>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-3" data-testid="social-preset-album">
            <div className="flex items-center justify-between">
              <h4 className="text-white font-bold">Album Drop</h4>
              <span className="text-gold-400 text-xs bg-gold-500/10 px-2 py-1 rounded">9:16</span>
            </div>
            <p className="text-gray-400 text-sm">Announce the album is out</p>
            <div className="bg-gray-900 rounded-lg p-3 text-sm text-gray-300 font-mono">
              THE GORDON PRINCIPLE — OUT NOW
            </div>
            <button
              onClick={() => navigator.clipboard.writeText('THE GORDON PRINCIPLE — OUT NOW\n\n21 tracks of soulful originals. Stream now on all platforms.\n\nbrooksgordon.com/store')}
              className="w-full px-4 py-2 bg-gold-500/20 hover:bg-gold-500/30 text-gold-400 rounded-lg text-sm font-medium transition-colors"
            >
              Copy Caption
            </button>
          </div>

          <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-3" data-testid="social-preset-single">
            <div className="flex items-center justify-between">
              <h4 className="text-white font-bold">Featured Single — &ldquo;Real Love&rdquo;</h4>
              <span className="text-gold-400 text-xs bg-gold-500/10 px-2 py-1 rounded">9:16</span>
            </div>
            <p className="text-gray-400 text-sm">Highlight a single track</p>
            <div className="bg-gray-900 rounded-lg p-3 text-sm text-gray-300 font-mono">
              &ldquo;Real Love&rdquo; — from The Gordon Principle
            </div>
            <button
              onClick={() => navigator.clipboard.writeText('"Real Love" — from The Gordon Principle\n\nStream now on Spotify, Apple Music & YouTube.\n\nbrooksgordon.com')}
              className="w-full px-4 py-2 bg-gold-500/20 hover:bg-gold-500/30 text-gold-400 rounded-lg text-sm font-medium transition-colors"
            >
              Copy Caption
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
